import { Button, ScrollArea, Stack } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Transit } from '@e-origin/shared';

import { LeavePageNotification, StickyContainer } from '@components';
import { GoodsItemsDetails } from './GoodsItemsDetails/GoodsItemsDetails';
import { HouseDetails } from './HouseDetails/HouseDetails';
import { useTransitViewHouseFormInitializer } from './useTransitViewHouseFormInitializer';

interface TransitViewHouseProps {
  editDisabled: boolean;
  editDisabledConditionalForArrival: boolean;
  transit: Transit;
}

enum TransitViewContext {
  HOUSE = 'HOUSE',
  GOODS = 'GOODS',
}

export const TransitViewHouse: React.FC<TransitViewHouseProps> = ({ editDisabled, editDisabledConditionalForArrival, transit }) => {
  const { buildFormValues, form, onSubmit } = useTransitViewHouseFormInitializer({ transit });

  const routeLocation = useLocation();
  const queryParams = useRef<URLSearchParams>(new URLSearchParams(routeLocation.search));
  const [context, setContext] = useState<TransitViewContext>(TransitViewContext.HOUSE);

  useEffect(() => {
    queryParams.current = new URLSearchParams(routeLocation.search);

    const queryContext = queryParams.current.get('context');
    setContext(queryContext ? (queryContext as TransitViewContext) : TransitViewContext.HOUSE);
  }, [routeLocation]);

  useEffect(() => {
    form.setValues(buildFormValues(transit));
    form.resetDirty({});
  }, [transit]);

  return (
    <>
      <LeavePageNotification hasUnsavedChanges={form.isDirty()} ignoreSearchParams={true} />

      <ScrollArea h="100%" offsetScrollbars="y">
        <form onSubmit={form.onSubmit(onSubmit)}>
          <Stack mx="xl">
            {[TransitViewContext.HOUSE, null].includes(context) && (
              <HouseDetails form={form} editDisabled={editDisabled} editDisabledConditionalForArrival={editDisabledConditionalForArrival} />
            )}

            {context === TransitViewContext.GOODS && (
              <GoodsItemsDetails form={form} editDisabled={editDisabled} editDisabledConditionalForArrival={editDisabledConditionalForArrival} />
            )}
          </Stack>
          <StickyContainer>
            <Button
              type="submit"
              disabled={(editDisabled && editDisabledConditionalForArrival) || !form.isValid()}
              variant="filled"
              size="lg"
              data-pw="save-transit-btn"
            >
              Save
            </Button>
          </StickyContainer>
        </form>
      </ScrollArea>
    </>
  );
};
